import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { CurrencySymbolComponent } from '../../../core/components/currency-symbol/currency-symbol.component';
import { AmountPipe } from '../../../core/pipes/amount.pipe';
import { AuthService } from '../../auth/auth.service';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';
import { ActivityTimelineComponent } from '../widgets/shared/activity-timeline/activity-timeline.component';
import { ContractExpiryTrackerComponent } from '../widgets/shared/contract-expiry-tracker/contract-expiry-tracker.component';
import { KpiCard, KpiCardGridComponent } from '../widgets/shared/kpi-card-grid/kpi-card-grid.component';
import { WorkQueueListComponent } from '../widgets/shared/work-queue-list/work-queue-list.component';

@Component({
  selector: 'app-regulatory-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    NgClass,
    DatePipe,
    RouterModule,
    GlobalFilterPanelComponent,
    KpiCardGridComponent,
    ContractExpiryTrackerComponent,
    ActivityTimelineComponent,
    WorkQueueListComponent,
    AmountPipe,
    CurrencySymbolComponent,
  ],
  templateUrl: './regulatory-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class RegulatoryDashboardComponent {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  readonly dashboardType = 'regulatory';
  filters: any = {};

  // expiring-compliances widget added: shows risk meter + items list
  complianceData: any = null;
  documentData: any = null;
  expiringComplianceData: any = null;
  authorityData: any = null;

  loadingCompliance = false;
  loadingDocument = false;
  loadingExpiring = false;
  loadingAuthority = false;

  complianceKpis: KpiCard[] = [];

  readonly filterConfig: FilterConfig = {
    community: true,
    period: true,
    dateRange: false,
    status: false,
    priority: false,
  };

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadAll();
    this.cdr.markForCheck();
  }

  get hasCompliancePermission(): boolean {
    return this.auth.canAccess(['company_compliance_view']);
  }

  private loadAll(): void {
    this.loadCompliance();
    if (this.hasCompliancePermission) {
      this.loadDocumentStatus();
      this.loadExpiringCompliances();
      this.loadAuthorityStatus();
    }
  }

  private loadAuthorityStatus(): void {
    this.loadingAuthority = true;
    this.engine
      .getWidgetData(this.dashboardType, 'regulatoryAuthorityStatus', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.authorityData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingAuthority = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingAuthority = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadExpiringCompliances(): void {
    this.loadingExpiring = true;
    this.engine
      .getWidgetData(this.dashboardType, 'complianceExpiring30Days', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.expiringComplianceData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingExpiring = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingExpiring = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadCompliance(): void {
    this.loadingCompliance = true;
    this.engine
      .getWidgetData(this.dashboardType, 'complianceOverview', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.complianceData = d;

          // Map license_status → display value + colors.
          // 'unknown' = no license_expiry on the company profile (different from 'expired'!)
          const status = d.license_status as 'valid' | 'expiring_soon' | 'expired' | 'unknown' | undefined;
          const statusLabel = ({ valid: 'Valid', expiring_soon: 'Expiring', expired: 'Expired', unknown: 'Not Set' } as Record<string, string>)[status ?? 'unknown'] ?? 'Not Set';
          const statusColor = status === 'valid' ? 'text-green-600' : status === 'expiring_soon' ? 'text-amber-600' : status === 'expired' ? 'text-red-600' : 'text-gray-500';
          const statusBg = status === 'valid' ? 'bg-green-50' : status === 'expiring_soon' ? 'bg-amber-50' : status === 'expired' ? 'bg-red-50' : 'bg-gray-50';

          // Days-to-expiry display:
          //   null      → "—" (no license on file)
          //   negative  → "Nd overdue"   (license already expired)
          //   ≥ 0       → "Nd"           (days remaining)
          const days = d.license_days_left;
          const daysRounded = days == null ? null : Math.round(days);
          const daysValue = daysRounded == null ? '—' : daysRounded < 0 ? `${Math.abs(daysRounded)} d overdue` : `${daysRounded} d`;
          const daysColor = days == null ? 'text-gray-500' : days < 0 ? 'text-red-600' : days <= 30 ? 'text-amber-600' : 'text-blue-600';
          const daysBg = days == null ? 'bg-gray-50' : days < 0 ? 'bg-red-50' : days <= 30 ? 'bg-amber-50' : 'bg-blue-50';

          this.complianceKpis = [
            {
              label: 'Company Trade License',
              value: statusLabel,
              icon: 'verified',
              color: statusColor,
              bgColor: statusBg,
            },
            {
              label: 'License Days Left',
              value: daysValue,
              icon: 'event',
              color: daysColor,
              bgColor: daysBg,
            },
            {
              label: 'Active Vendors',
              value: d.vendor_total ?? 0,
              icon: 'store',
              color: 'text-indigo-600',
              bgColor: 'bg-indigo-50',
            },
          ];
          this.loadingCompliance = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingCompliance = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadDocumentStatus(): void {
    this.loadingDocument = true;
    this.engine
      .getWidgetData(this.dashboardType, 'documentStatus', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.documentData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingDocument = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingDocument = false;
          this.cdr.markForCheck();
        },
      });
  }

  getSeverityColor(severity: string): string {
    const map: any = {
      critical: 'text-red-700 bg-red-50 border-red-200',
      high: 'text-orange-700 bg-orange-50 border-orange-200',
      medium: 'text-yellow-700 bg-yellow-50 border-yellow-200',
      low: 'text-blue-700 bg-blue-50 border-blue-200',
    };
    return map[severity] ?? 'text-gray-700 bg-gray-50 border-gray-200';
  }

  /** Risk meter color — bar tints redder as % expiring rises. */
  getRiskBarColor(pct: number): string {
    if (pct >= 30) return 'bg-red-600';
    if (pct >= 15) return 'bg-orange-500';
    if (pct >= 5) return 'bg-amber-500';
    return 'bg-green-500';
  }

  getRiskTextClass(pct: number): string {
    if (pct >= 30) return 'text-red-700';
    if (pct >= 15) return 'text-orange-700';
    if (pct >= 5) return 'text-amber-700';
    return 'text-green-700';
  }

  getScopeIcon(scope: string): string {
    return ({ association: 'apartment', unit: 'door_front', vendor: 'store' } as Record<string, string>)[scope] ?? 'inventory_2';
  }

  getScopeLabel(scope: string): string {
    return ({ association: 'Community', unit: 'Unit', vendor: 'Vendor' } as Record<string, string>)[scope] ?? scope;
  }

  // ── Regulatory authority widget — color mapping per status flag ─────
  getAuthorityRowClass(status: string): string {
    return (
      (
        {
          valid: 'border-green-100 bg-green-50/30',
          warning: 'border-amber-100 bg-amber-50/30',
          missing: 'border-red-100 bg-red-50/30',
          info: 'border-blue-100 bg-blue-50/30',
        } as Record<string, string>
      )[status] ?? 'border-gray-100'
    );
  }

  getAuthorityIconBg(status: string): string {
    return ({ valid: 'bg-green-100', warning: 'bg-amber-100', missing: 'bg-red-100', info: 'bg-blue-100' } as Record<string, string>)[status] ?? 'bg-gray-100';
  }

  getAuthorityIconColor(status: string): string {
    return ({ valid: 'text-green-700', warning: 'text-amber-700', missing: 'text-red-700', info: 'text-blue-700' } as Record<string, string>)[status] ?? 'text-gray-500';
  }

  getAuthorityBadgeClass(status: string): string {
    return (
      (
        {
          valid: 'bg-green-100 text-green-700',
          warning: 'bg-amber-100 text-amber-700',
          missing: 'bg-red-100 text-red-700',
          info: 'bg-blue-100 text-blue-700',
        } as Record<string, string>
      )[status] ?? 'bg-gray-100 text-gray-600'
    );
  }
}
